/**
 * Wires `gallery.model.ts` to the inventory's read and its two writes.
 *
 * Calls `fetchGalleryInventory` and `useWorkThumbnails` for the list, and
 * `answerConsignment` to accept or decline an offer; the sale itself is
 * `useSellWizardController.ts`, and what this hook holds of it is only which
 * consignment the wizard is open on and `soldOne`, the re-read it triggers
 * once the registry has the sale. Both writes are followed by a fresh read,
 * because accepting widens what the gallery may see and selling removes the
 * row from this relation entirely.
 *
 * The state the model cannot hold is the chosen artist, which offer is
 * mid-flight, the open consignment id and the round counter. `selling` hands
 * the wizard the raw `ConsignedWork`, not the display row, because the
 * wizard needs the numbers and the row carries only strings.
 */

import { useCallback, useEffect, useState } from "react";
import type { ConsignedWork } from "@ondo/shared";

import { answerConsignment, fetchGalleryInventory } from "../lib/api.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { describeInventoryError, toInventoryView } from "./gallery.model.ts";
import type { InventoryView } from "./gallery.model.ts";

export interface InventoryController {
  view: InventoryView;
  /** Narrow the list to one artist. "" is all of them. */
  chooseArtist: (artist: string) => void;
  /** Take a work offered to this gallery, or hand it back unanswered. */
  answer: (consignmentId: string, accept: boolean) => void;
  /** Which offer is mid-flight, so its two buttons can be disabled. */
  answering: string | null;
  /**
   * The consignment the Sell wizard is open on, or null.
   *
   * The whole `ConsignedWork` rather than the `InventoryRow` beside it, because
   * the wizard needs the RAW terms — `askingMinor`, `currency`, `floorMinor`,
   * `note` — and the row deliberately carries only the strings it prints. A row
   * built for display is the wrong place to read a number out of.
   */
  selling: ConsignedWork | null;
  openSell: (consignmentId: string) => void;
  closeSell: () => void;
  /** Called by the wizard once the registry holds the sale. Re-reads the list. */
  soldOne: () => void;
}

/**
 * The works consigned to this gallery.
 *
 * One read and its photographs, plus the one write this screen carries.
 *
 * TWO WRITES NOW, AND THEY ARE DIFFERENT SHAPES. Answering an offer is a
 * decision about a row — take this picture or do not — and it is settled on the
 * row itself. Selling is a decision about an object, with a price, a buyer and
 * terms that bind for years, so it opens a dialog of its own; what this hook
 * holds of it is only WHICH consignment is open and what to do once the registry
 * has the sale. The steps themselves are `useSellWizardController`.
 *
 * This file contains no flag. Whether the Sell control is live is
 * `capabilities.transferWork`, which the screen is passed and the server
 * answers from `ondo.sellable_work_ids()`.
 *
 * @param workBase '/gallery/ONDO-6624/work' — where a row links to.
 */
export function useInventoryController(workBase: string): InventoryController {
  const [consignments, setConsignments] = useState<ConsignedWork[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const images = useWorkThumbnails(consignments, (each) => ({
    id: each.workId,
    imagePath: each.imagePath,
  }));
  const [artist, setArtist] = useState("");
  const [answering, setAnswering] = useState<string | null>(null);
  const [sellingId, setSellingId] = useState<string | null>(null);
  /** Bumped to re-read after an answer, so the list reflects Postgres. */
  const [round, setRound] = useState(0);

  useEffect(() => {
    let active = true;

    fetchGalleryInventory()
      .then((found) => {
        if (active) setConsignments(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeInventoryError(cause));
      });

    return () => {
      active = false;
    };
  }, [round]);

  /**
   * Answering an offer, then RE-READING rather than patching state.
   *
   * The row that comes back is not the row that went in: accepting widens what
   * this gallery may read, and `ondo.gallery_inventory()` is the only thing that
   * knows the result. Editing the array locally would be this screen deciding
   * what the database did — and a decline removes the row entirely, which local
   * state would have to guess at.
   */
  const answer = useCallback((consignmentId: string, accept: boolean) => {
    setAnswering(consignmentId);

    answerConsignment(consignmentId, accept)
      .then(() => {
        setConsignments(null);
        setError(null);
        setRound((n) => n + 1);
      })
      .catch((cause: unknown) => {
        setError(describeInventoryError(cause));
      })
      .finally(() => {
        setAnswering(null);
      });
  }, []);

  /**
   * Re-reads after a sale rather than dropping the row locally.
   *
   * The same argument `answer` makes, one step further on. `ondo.effect_sale()`
   * ENDS the consignment, so the sold work does not change on this screen — it
   * leaves `ondo.gallery_inventory()` entirely and reappears on Sold Works,
   * which is a different relation. Splicing the array here would be this screen
   * deciding what the database did.
   */
  const closeSell = useCallback(() => {
    setSellingId(null);
  }, []);

  const soldOne = useCallback(() => {
    setSellingId(null);
    setConsignments(null);
    setError(null);
    setRound((n) => n + 1);
  }, []);

  return {
    view: toInventoryView({ consignments, error, images, workBase, artist }),
    chooseArtist: setArtist,
    answer,
    answering,
    selling:
      consignments?.find((each) => each.consignmentId === sellingId) ?? null,
    openSell: setSellingId,
    closeSell,
    soldOne,
  };
}
